/**
 * The UI's path onto the semantic layer (ADR-009 §3.4).
 *
 * Discrete editor operations (the layer panel, the Delete key) dispatch
 * intents with `actor: { kind: 'user' }` through the same intent registry,
 * validation, `TransactionCommand`, history entry, revision ledger and
 * field journal as agent transactions. What the user path leaves out is the
 * agent envelope, and it does so on purpose:
 *
 * - it is synchronous, so React state, focus and selection update in the
 *   same task as before;
 * - intents are built by typed UI code, so there is no JSON schema check,
 *   idempotency key or base revision (the user always edits the current
 *   revision);
 * - validation runs with the user's ActorPolicy (policyFor), which keeps the
 *   native lock and kind semantics the UI has always had;
 * - selection is left to the native commands (`nativeSelection`), and the
 *   history label is the UI's own label, so undo labels do not change;
 * - the journal projects only the layers in the transaction's footprint.
 *   It still walks the whole layer tree twice per commit to detect created
 *   and deleted layers: O(layers), no projection, well below a canvas redraw.
 *
 * This module must stay free of the agent-protocol validators and the vector
 * runtime so the editor's initial bundle does not grow with them.
 */
import type { ActorRef, FieldChange, Intent, LayerId, RejectionCode } from '@maipo/agent-protocol';
import { layerNestingDepth, MAX_LAYER_NESTING, type EditorDocument } from '../core/model';
import type { DocumentSession } from '../core/session';
import { layerCreate, layerDelete, layerGroup, layerReorder, layerUngroup, layerUpdate } from './intents/layer';
import { slotFill, slotMark, slotRename, slotUnmark } from './intents/slots';
import { commandRun } from './intents/command';
import { maskClip, maskInvert, maskRemove, maskUpdate } from './intents/masks';
import { effectSet } from './intents/properties';
import { diff, snapshot } from './journal';
import { normalizeLayerId } from './ledger';
import { createRegistry, IntentRejection, mergeFootprints, policyFor, type AliasTable, type CommandDefinition, type Diagnostic, type Footprint, type IntentRegistry } from './registry';
import { TransactionCommand, type TransactionOptions, type TransactionStage, type TxMeta } from './transaction';

/** Intents the UI dispatches. Adding one here requires a byte-parity test against the direct command it replaces. */
export const USER_INTENT_DEFINITIONS = [
  layerUpdate, layerReorder, layerGroup, layerUngroup, layerDelete,
  maskClip, maskUpdate, maskInvert, maskRemove,
  effectSet,
  commandRun,
  // Agent parity phase 2: "New raster layer" (layer.create { kind: 'raster' }) and the Fill panel's slot edits.
  layerCreate,
  slotMark, slotUnmark, slotRename, slotFill,
] as const;

const USER_REGISTRY: IntentRegistry = createRegistry(USER_INTENT_DEFINITIONS);

/** Actors that may use the synchronous UI path. Agents and add-ons go through the envelope (`dispatch`). */
export type LocalActor = Extract<ActorRef, { kind: 'user' } | { kind: 'replay' }>;

export type UserDispatchOutcome =
  | { status: 'committed'; command: TransactionCommand; changes: FieldChange[] }
  | { status: 'no-op' }
  | { status: 'rejected'; code: RejectionCode; message: string; intentIndex?: number; layer?: LayerId };

let sequence = 0;

/** A session-unique transaction ID for locally originated transactions. */
export function localTxId(prefix: 'user' | 'revert'): string {
  sequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${sequence}`;
}

function definitionOf(registry: IntentRegistry, intent: Intent): CommandDefinition {
  const definition = registry.get(intent.type);
  if (!definition) throw new IntentRejection('UNSUPPORTED_COMMAND', `Intent type ${intent.type} is not available to the editor UI.`);
  return definition;
}

function firstError(diagnostics: readonly Diagnostic[]): Extract<Diagnostic, { severity: 'error' }> | undefined {
  return diagnostics.find((diagnostic): diagnostic is Extract<Diagnostic, { severity: 'error' }> => diagnostic.severity === 'error');
}

/** Layers whose records the journal needs: everything the footprint names (the root is not a layer). */
function journalScope(document: EditorDocument, footprint: Footprint): Set<LayerId> {
  return new Set([...footprint.reads, ...footprint.writes].map((id) => normalizeLayerId(document, id)).filter((id) => id !== 'root'));
}

/**
 * Executes one locally built transaction as a single history entry and fills
 * its journal. Shared by the UI path and by revert.
 */
export function commitLocalTransaction(session: DocumentSession, meta: TxMeta, stages: readonly TransactionStage[], options: TransactionOptions): UserDispatchOutcome {
  const document = session.document;
  const scope = journalScope(document, meta.footprint);
  const before = snapshot(document, (id) => scope.has(id));
  const transaction = new TransactionCommand(meta, stages, options);
  // Throws while a live gesture is active, exactly as a direct execute did.
  const execution = session.history.execute(document, transaction);
  if (execution.outcome === 'rejected') {
    const rejection = transaction.rejection;
    return { status: 'rejected', code: rejection?.code ?? 'COMMAND_REJECTED', message: rejection?.message ?? execution.reason, ...(rejection ? { intentIndex: rejection.intentIndex } : {}), ...(rejection?.layer === undefined ? {} : { layer: rejection.layer }) };
  }
  if (execution.outcome === 'no-op') return { status: 'no-op' };
  const created = new Set(Object.values(meta.aliases));
  const after = snapshot(document, (id) => scope.has(id) || created.has(id) || !before.ids.has(id));
  const writes = new Set<LayerId>([...meta.footprint.writes, ...created]);
  meta.journal = diff(before, after, writes).changes;
  return { status: 'committed', command: transaction, changes: meta.journal };
}

/**
 * Dispatches intents for the user (or a replay of the user's recorded
 * actions) synchronously. Rejections change nothing and are returned, never
 * thrown; callers that ignored a native command's rejection may ignore these.
 */
export function dispatchUser(session: DocumentSession, intents: readonly Intent[], label: string, actor: LocalActor = { kind: 'user' }): UserDispatchOutcome {
  const document = session.document;
  const policy = policyFor(actor);
  let footprint: Footprint;
  try {
    const definitions = intents.map((intent) => definitionOf(USER_REGISTRY, intent));
    footprint = mergeFootprints(intents.map((intent, index) => (definitions[index] as CommandDefinition).footprint(document, intent, new Map())));
    for (const [intentIndex, intent] of intents.entries()) {
      const failure = firstError((definitions[intentIndex] as CommandDefinition).validate(document, intent, { actor, policy, aliases: new Map(), pending: new Set() }));
      if (failure) return { status: 'rejected', code: failure.code, message: failure.message, intentIndex, ...(failure.layer === undefined ? {} : { layer: failure.layer }) };
    }
  } catch (error) {
    if (error instanceof IntentRejection) return { status: 'rejected', code: error.code, message: error.message, ...(error.layer === undefined ? {} : { layer: error.layer }) };
    throw error;
  }
  const stages: TransactionStage[] = intents.map((intent, intentIndex) => ({
    intentIndex,
    build: (current: EditorDocument, aliases: AliasTable) => {
      const definition = definitionOf(USER_REGISTRY, intent);
      const failure = firstError(definition.validate(current, intent, { actor, policy, aliases, pending: new Set() }));
      if (failure) throw new IntentRejection(failure.code, failure.message, failure.layer);
      return definition.compile(current, intent, undefined, aliases);
    },
  }));
  const meta: TxMeta = {
    txId: localTxId('user'),
    actor,
    label,
    createdAt: new Date().toISOString(),
    baseRevision: session.currentRevision,
    intents: structuredClone([...intents]),
    footprint,
    journal: [],
    aliases: {},
  };
  // The one persistence invariant the UI can break through an intent (group, paste via command.run): nesting depth. O(layers).
  const depthBefore = layerNestingDepth(document);
  const check = (current: EditorDocument): { code: RejectionCode; message: string } | null => {
    const depth = layerNestingDepth(current);
    return depth > MAX_LAYER_NESTING && depth > depthBefore
      ? { code: 'LIMIT_EXCEEDED', message: `Layer nesting depth ${depth} exceeds the limit of ${MAX_LAYER_NESTING}; a project with groups nested deeper cannot be saved.` }
      : null;
  };
  return commitLocalTransaction(session, meta, stages, { restoreSelection: false, nativeSelection: true, check });
}
